import { ArticleType, Category } from '@wix/answers-api';
import { Box, Divider, Heading, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { TimerSmall } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import React, { FC } from 'react';

import { articleSearchResultItemDataHooks } from './dataHooks';
import { SearchResultItemBadges } from './SearchResultItemBadges';
import css from './styles.scss';

// TODO: Move to centralized place in shared components library.
export enum Theme {
  Mobile = 'mobile',
  Desktop = 'desktop',
  Widget = 'widget',
}

export type SearchResultItemProps = {
  id: string;
  title: string;
  sanitizedDescription?: string;
  articleType?: ArticleType;
  readingTimeInMinutes?: number;
  distance?: number;
  rootTopic: Category;
  isGeneratedAnswerDisplayed?: boolean;
  LinkComponent: React.ComponentType<any>;
  articleLinkProps?: Record<string, any>;
  badgeLinkProps?: Record<string, any>;
  theme: Theme;
  onArticleTopicBadgeClick: (rootTopic: Category) => void;
  onArticleTypeTagClick: (clickedText: string, url: string) => void;
  onArticleClick: () => void;
};

export const SearchResultItem: FC<SearchResultItemProps> = ({
  title,
  sanitizedDescription = '',
  articleType,
  readingTimeInMinutes,
  rootTopic,
  onArticleTopicBadgeClick,
  onArticleClick,
  onArticleTypeTagClick,
  isGeneratedAnswerDisplayed,
  LinkComponent,
  articleLinkProps,
  badgeLinkProps,
  theme,
}) => {
  const isMobileView = theme === Theme.Mobile;
  const isWidgetView = theme === Theme.Widget;
  const isSmallScreen = isMobileView || isWidgetView;

  const { t } = useTranslation();

  const readingTimeInComp = (
    <Box verticalAlign="middle" marginInlineStart="SP4">
      <TimerSmall color="#868AA5" className={css.timerIcon} />
      <Text
        size="tiny"
        color="#868AA5"
        className={css.timeToRead}
        dataHook={articleSearchResultItemDataHooks.searchResultItemTimeToRead()}
      >
        {t('informative-article-time-to-read', {
          time: readingTimeInMinutes,
        })}
      </Text>
    </Box>
  );

  return (
    <Box
      className={css.searchResultItem}
      direction="vertical"
      align="left"
      width="100%"
      dataHook={articleSearchResultItemDataHooks.searchResultItem()}
      padding={isGeneratedAnswerDisplayed ? 'SP3 0' : 'SP4 SP1 0 SP3'}
    >
      <SearchResultItemBadges
        rootTopic={rootTopic}
        articleType={articleType!}
        onArticleTopicBadgeClick={onArticleTopicBadgeClick}
        onArticleTypeTagClick={onArticleTypeTagClick}
        LinkComponent={LinkComponent}
        badgeLinkProps={badgeLinkProps}
      />
      <Box direction="vertical" width="100%" className={css.searchResultItemBody} paddingBottom="SP3">
        <Box align="space-between">
          <LinkComponent
            className={css.titleLink}
            onClick={onArticleClick}
            {...articleLinkProps}
            dataHook={articleSearchResultItemDataHooks.searchResultItemTitleLink()}
          >
            <Box margin="SP1 0">
              <Heading
                size="medium"
                dataHook={articleSearchResultItemDataHooks.searchResultItemTitle()}
                className={css.title}
              >
                {title}
              </Heading>
            </Box>
          </LinkComponent>
          {!isSmallScreen && !sanitizedDescription?.length && readingTimeInComp}
        </Box>
        {sanitizedDescription && (
          <Box
            className={css.descriptionAndReadingTimeWrapper}
            direction="horizontal"
            verticalAlign="middle"
            align="space-between"
          >
            <LinkComponent
              className={classNames(css.descriptionLink, {
                [css.descriptionLinkWide]: isGeneratedAnswerDisplayed,
              })}
              onClick={onArticleClick}
              {...articleLinkProps}
            >
              <Text
                dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
                weight="thin"
                size={isSmallScreen ? 'small' : 'medium'}
                maxLines={2}
                ellipsis={true}
                showTooltip={false}
                dataHook={articleSearchResultItemDataHooks.searchResultItemDescription()}
              />
            </LinkComponent>
            {!isSmallScreen && readingTimeInComp}
          </Box>
        )}
      </Box>
      {isSmallScreen && <Divider />}
    </Box>
  );
};
